import { useFirebase, isSuccess } from 'src/firebase'

// Read from the Realtime Database because `server` takes `appVersion`/`deviceModel` as mutation
// arguments but exposes no field to read them back.
type DeviceRecord = {
  appVersion?: string
  deviceModel?: string
  platform?: string
  osVersion?: string
  updatedAt?: number
}

type OnlineStatusRecord = {
  lastSeenAt?: number
  connections?: Record<string, { lastActivity: number }>
}

const ONLINE_WINDOW_MS = 60 * 60 * 1000

export type ParticipantActivity =
  | { status: 'loading' }
  | { status: 'online' }
  | { status: 'seen'; at: number }
  | { status: 'unknown' }

export const useParticipantActivity = (uid: string): ParticipantActivity => {
  const request = useFirebase<OnlineStatusRecord>(`onlineStatus/${uid}`)

  if (!isSuccess(request)) {
    return { status: 'loading' }
  }

  const connections = Object.values(request.data?.connections ?? {})
  const lastActivity = connections.reduce((latest, connection) => Math.max(latest, connection.lastActivity ?? 0), 0)

  if (lastActivity && Date.now() - lastActivity < ONLINE_WINDOW_MS) {
    return { status: 'online' }
  }

  // The newer of the two: a live connection beats the disconnect that wrote `lastSeenAt`.
  const seenAt = Math.max(lastActivity, request.data?.lastSeenAt ?? 0)

  return seenAt ? { status: 'seen', at: seenAt } : { status: 'unknown' }
}

export type ParticipantDevice =
  | { status: 'loading' }
  | { status: 'unknown' }
  | { status: 'known'; appVersion: string | null; device: string | null }

export const useParticipantDevice = (uid: string): ParticipantDevice => {
  const request = useFirebase<Record<string, DeviceRecord>>(`metaUserData/${uid}/devices`)

  if (!isSuccess(request)) {
    return { status: 'loading' }
  }

  const latest = Object.values(request.data ?? {}).reduce<DeviceRecord | null>(
    (newest, device) => ((device.updatedAt ?? 0) > (newest?.updatedAt ?? 0) ? device : newest),
    null
  )

  if (!latest) {
    return { status: 'unknown' }
  }

  const platform = [latest.platform, latest.osVersion].filter(Boolean).join(' ')

  return {
    status: 'known',
    appVersion: latest.appVersion ?? null,
    device: [latest.deviceModel, platform && `(${platform})`].filter(Boolean).join(' ') || null,
  }
}
